import { useState } from 'react';
import type { UpstreamConfig } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useUpdateUpstream } from '../api/useUpstreams.ts';
import { useT } from '../i18n/index.ts';
import { Button } from '../ui/index.ts';
import {
  ModelDiscoveryFields,
  draftFromModelDiscovery,
  modelDiscoveryFromDraft,
  modelDiscoveryServerError,
  validateModelDiscoveryDraft,
} from './ModelDiscoveryFields.tsx';
import { coerceErrorMessage, useToast } from './Toast.tsx';

/**
 * "模型发现" cell on the upstream detail configuration card (#1658 B).
 *
 * Unlike the card's inline blur-to-PATCH text fields, `list` mode is a
 * three-field compound value the gateway validates as one object — so
 * this field is draft-based with a single save button that PATCHes one
 * atomic `model_discovery` document. It owns its own mutation instead of
 * the card's shared `handlePatch` because server validation errors here
 * are mapped to field-local operator copy rather than the card's raw
 * `code: message` banner.
 */

interface ModelDiscoveryConfigFieldProps {
  readonly upstream: UpstreamConfig;
}

export function ModelDiscoveryConfigField({
  upstream,
}: ModelDiscoveryConfigFieldProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const update = useUpdateUpstream(upstream.id);
  const [draft, setDraft] = useState(() => draftFromModelDiscovery(upstream.model_discovery));
  const [error, setError] = useState<string | null>(null);

  async function handleSave(): Promise<void> {
    setError(null);
    const validationError = validateModelDiscoveryDraft(draft, t);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    try {
      await update.mutateAsync({ model_discovery: modelDiscoveryFromDraft(draft) });
      toast.success(t('upstream_detail.config.model_discovery.saved'));
    } catch (err) {
      const message =
        modelDiscoveryServerError(t, err) ??
        (err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err));
      setError(message);
      toast.error(message);
    }
  }

  return (
    <div
      className="flex flex-col gap-4 rounded-lg bg-well p-4 sm:col-span-2"
      data-testid="upstream-config-model-discovery"
    >
      <div className="flex flex-col gap-1">
        <span className="text-label text-paper-mid">
          {t('upstream_detail.config.model_discovery')}
        </span>
        <span className="text-dense text-paper-low">
          {t('upstream_detail.config.model_discovery.hint')}
        </span>
      </div>
      <ModelDiscoveryFields
        draft={draft}
        onChange={setDraft}
        disabled={update.isPending}
        idPrefix="upstream-config-discovery"
      />
      {error !== null ? (
        <p
          className="m-0 text-label text-bad"
          role="alert"
          data-testid="upstream-config-discovery-error"
        >
          {error}
        </p>
      ) : null}
      <div>
        <Button
          size="sm"
          variant="primary"
          onClick={() => void handleSave()}
          loading={update.isPending}
          disabled={update.isPending}
          data-testid="upstream-config-discovery-save"
        >
          {update.isPending ? t('common.saving') : t('upstream_detail.config.model_discovery.save')}
        </Button>
      </div>
    </div>
  );
}
